import z from "zod";

const workflowErrorTypeValues = [
  "PROJECT_FLOW_NOT_FOUND",
  "INHERITED_PROJECT_FLOW_NOT_FOUND",
  "RECORD_ATTEMPT_WORKFLOW_FAILED",
  "TASK_COMPLETION_WORKFLOW_FAILED",
  "TASK_READINESS_COHORT_TRUNCATED",
  "TASK_READINESS_RECONCILE_FAILED",
  "ATTEMPT_V2_PAYLOAD_INVALID",
  "INSTALL_STARTED_REQUESTED_PAYLOAD_INVALID",
  "ATTEMPT_OUTCOME_NOT_INTERPRETED",
  "ATTEMPT_REOPEN_TARGET_NOT_FOUND",
  "ATTEMPT_TERMINAL_COMPLETION_RACE_LOST",
  "EFFECT_WAVE_UNKNOWN_DEPENDENCY",
  "EFFECT_WAVE_CYCLE",
  "LIFECYCLE_REEVALUATION_APPLY_FAILED",
] as const;

const workflowErrorTypeSchema = z.enum(workflowErrorTypeValues);
export const WorkflowErrorType = workflowErrorTypeSchema.enum;
type WorkflowErrorType = z.infer<typeof workflowErrorTypeSchema>;

type ErrorDetails = {
  sentryDescription: string;
};

export type WorkflowErrors =
  | {
      errorType: typeof WorkflowErrorType.TASK_READINESS_RECONCILE_FAILED;
      data: { taskId: string };
    }
  | {
      errorType: typeof WorkflowErrorType.TASK_READINESS_COHORT_TRUNCATED;
      data: {
        scannedCount: number;
        pages: number;
      };
    }
  | {
      errorType: typeof WorkflowErrorType.RECORD_ATTEMPT_WORKFLOW_FAILED;
      data: {
        taskId: string;
        dedupeKey: string;
        runId: string;
        originalErrorName: string;
        originalErrorMessage: string;
        workflowVersion: string;
        failedStep: string;
        outcomeKind: string;
        sideEffectKeys: string[];
      };
    }
  | {
      errorType: typeof WorkflowErrorType.TASK_COMPLETION_WORKFLOW_FAILED;
      // An explicit allowlist: the free-text `reason` and any disposition commentary stay out of Sentry.
      data: {
        taskId: string;
        projectId: number;
        bidId: number | null;
        concurrencyKey: string;
        completionNonce: string;
        requestedAt: string;
        runId: string;
        failedStep: string;
        originalErrorName: string;
      };
    }
  | {
      errorType: typeof WorkflowErrorType.ATTEMPT_V2_PAYLOAD_INVALID;
      data: {
        taskId: string;
        dedupeKey: string;
        // Paths, never issue values: a rejected payload's values are caller-supplied free text.
        issuePaths: string[];
      };
    }
  | {
      errorType: typeof WorkflowErrorType.INSTALL_STARTED_REQUESTED_PAYLOAD_INVALID;
      data: {
        // Paths, never issue values: a rejected payload's values are caller-supplied free text.
        issuePaths: string[];
      };
    }
  | {
      errorType: typeof WorkflowErrorType.ATTEMPT_OUTCOME_NOT_INTERPRETED;
      data: {
        taskId: string;
        taskEventId: string;
        outcomeKind: string;
      };
    }
  | {
      errorType: typeof WorkflowErrorType.ATTEMPT_REOPEN_TARGET_NOT_FOUND;
      data: {
        taskId: string;
        taskEventId: string;
        projectId: number;
        targetTaskType: string;
      };
    }
  | {
      errorType: typeof WorkflowErrorType.ATTEMPT_TERMINAL_COMPLETION_RACE_LOST;
      data: {
        taskId: string;
        projectId: number;
        taskEventId: string;
        reason: string;
      };
    }
  | {
      errorType: typeof WorkflowErrorType.EFFECT_WAVE_UNKNOWN_DEPENDENCY;
      data: {
        effectKey: string;
        dependency: string;
      };
    }
  | {
      errorType: typeof WorkflowErrorType.EFFECT_WAVE_CYCLE;
      data: {
        effectKeys: string[];
      };
    }
  | {
      errorType: typeof WorkflowErrorType.LIFECYCLE_REEVALUATION_APPLY_FAILED;
      data: {
        kind: string;
        nonce: string;
        failed: number;
      };
    }
  | {
      errorType: typeof WorkflowErrorType.PROJECT_FLOW_NOT_FOUND;
      data: {
        projectFlowType: string;
      };
    }
  | {
      errorType: typeof WorkflowErrorType.INHERITED_PROJECT_FLOW_NOT_FOUND;
      data: {
        inheritedFrom: string;
      };
    };

export const workflowErrorDetails: Record<WorkflowErrorType, ErrorDetails> = {
  [WorkflowErrorType.PROJECT_FLOW_NOT_FOUND]: {
    sentryDescription: "Project flow not found for the specified type",
  },
  [WorkflowErrorType.INHERITED_PROJECT_FLOW_NOT_FOUND]: {
    sentryDescription: "Project flow not found for inherited from type",
  },
  [WorkflowErrorType.RECORD_ATTEMPT_WORKFLOW_FAILED]: {
    sentryDescription: "Record-attempt workflow exhausted its retries",
  },
  [WorkflowErrorType.TASK_COMPLETION_WORKFLOW_FAILED]: {
    sentryDescription:
      "The durable task-completion workflow exhausted its retries. The task may be COMPLETED with part of its tail unrun; repair is manual (Reevaluate project)",
  },
  [WorkflowErrorType.ATTEMPT_V2_PAYLOAD_INVALID]: {
    sentryDescription:
      "A task/attempt.requested.v2 payload failed its own event schema. The outcome and effect-key snapshot are the interpreter's only inputs, so the run is refused rather than executed on a partial payload",
  },
  [WorkflowErrorType.INSTALL_STARTED_REQUESTED_PAYLOAD_INVALID]: {
    sentryDescription:
      "A task/install-started.requested payload failed its own event schema. requestedAt is written onto Project.installStartedAt, so the run is refused rather than stamping an Invalid Date",
  },
  [WorkflowErrorType.ATTEMPT_OUTCOME_NOT_INTERPRETED]: {
    sentryDescription:
      "A committed attempt carried an outcome kind the v2 workflow has no interpreter arm for. Every kind in the union now has an arm, so this fires only if a new kind ships without one",
  },
  [WorkflowErrorType.ATTEMPT_REOPEN_TARGET_NOT_FOUND]: {
    sentryDescription:
      "A REOPEN_TASK attempt outcome named a target task type the project has no task of. reopenTaskByType only logs and returns undefined in that case, so the outcome went unexecuted",
  },
  [WorkflowErrorType.TASK_READINESS_RECONCILE_FAILED]: {
    sentryDescription:
      "The readiness reconcile after a recorded attempt failed. The underlying error is captured separately by the readiness service; this one fails the step so Inngest retries it",
  },
  [WorkflowErrorType.ATTEMPT_TERMINAL_COMPLETION_RACE_LOST]: {
    sentryDescription:
      "An attempt-driven TERMINAL completion lost a race — a concurrent request already claimed or completed the task. The outcome still holds; captured so production race monitoring keeps the signal the inline completion path raised on any race",
  },
  [WorkflowErrorType.EFFECT_WAVE_UNKNOWN_DEPENDENCY]: {
    sentryDescription: "A durable effect declared an `after` dependency on a key its effect list does not register",
  },
  [WorkflowErrorType.EFFECT_WAVE_CYCLE]: {
    sentryDescription: "A durable effect list's `after` dependencies form a cycle, so no wave order exists",
  },
  [WorkflowErrorType.LIFECYCLE_REEVALUATION_APPLY_FAILED]: {
    sentryDescription:
      "A lifecycle workflow's ownership re-evaluation left rows that failed to apply, so the reevaluate step is retried",
  },
  [WorkflowErrorType.TASK_READINESS_COHORT_TRUNCATED]: {
    sentryDescription:
      "Task readiness reconcile hit its page ceiling, leaving part of the cohort unvisited. It re-fires every 15 minutes until the cohort shrinks or the scan bound is raised — raise COHORT_SCAN_LIMIT/MAX_PAGES or add cursor persistence (ODD-7827 carries the measured headroom)",
  },
};
